import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, gql } from "@apollo/client";
import ProfileForm, { ProfileFields } from "./ProfileForm";

const CREATE_USER_PROFILE = gql`
  mutation OnboardingCreateProfile($userId: Int!, $fullName: String, $displayName: String, $hometown: String, $school: String, $work: String, $interests: String, $bio: String, $preferredSaunaTempF: Int) {
    createUserProfile(input: {
      userProfile: {
        userId: $userId
        fullName: $fullName
        displayName: $displayName
        hometown: $hometown
        school: $school
        work: $work
        interests: $interests
        bio: $bio
        preferredSaunaTempF: $preferredSaunaTempF
      }
    }) {
      userProfile { id }
    }
  }
`;

const CREATE_ACKNOWLEDGED_TERMS = gql`
  mutation OnboardingAckTerms($userId: Int!) {
    createAcknowledgedTerm(input: {
      acknowledgedTerm: { userId: $userId, isHost: true, isGuest: true }
    }) {
      acknowledgedTerm { id }
    }
  }
`;

const UPDATE_SMS_PREF = gql`
  mutation OnboardingSetSmsPref($userId: Int!, $enabled: Boolean!) {
    updateUserById(input: { id: $userId, userPatch: { smsNotificationsEnabled: $enabled } }) {
      user { id }
    }
  }
`;

interface Props {
  userId: number;
  phone?: string | null;
  onDismiss: () => void;
}

export default function OnboardingModal({ userId, phone, onDismiss }: Props) {
  const [fields, setFields] = useState<ProfileFields>({
    fullName: "", displayName: "", hometown: "", school: "", work: "", interests: "", bio: "", preferredSaunaTempF: "",
  });
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createProfile] = useMutation(CREATE_USER_PROFILE, {
    refetchQueries: ["GetUserProfile", "NavMe"],
  });
  const [createAck] = useMutation(CREATE_ACKNOWLEDGED_TERMS);
  const [updateSmsPref] = useMutation(UPDATE_SMS_PREF);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const { errors } = await createProfile({
        variables: {
          userId,
          ...fields,
          preferredSaunaTempF: fields.preferredSaunaTempF ? parseInt(fields.preferredSaunaTempF, 10) : null,
        },
      });
      if (errors) { setError(errors[0]?.message ?? "Failed to save"); return; }
      await Promise.all([
        createAck({ variables: { userId } }),
        updateSmsPref({ variables: { userId, enabled: smsOptIn } }),
      ]);
      onDismiss();
    } catch (err: any) {
      setError(err?.message ?? "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}>
      <div style={modal}>
        <button onClick={onDismiss} style={closeBtn} aria-label="Close">✕</button>
        <h2 style={{ margin: "0 0 6px", fontSize: 20 }}>Welcome — tell hosts a bit about yourself</h2>
        <p style={{ margin: "0 0 20px", fontSize: 14, color: "#6b7280" }}>
          Hosts use this to get to know who's booking their sauna.
        </p>
        {error && <p style={{ color: "#ef4444", fontSize: 14, margin: "0 0 12px" }}>{error}</p>}
        <form onSubmit={handleSave}>
          <ProfileForm fields={fields} onChange={setFields} />

          <div style={termsBox}>
            <p style={{ margin: "0 0 8px", fontWeight: 600, fontSize: 13, color: "#111827" }}>Terms &amp; Conditions</p>
            <p style={{ margin: 0, fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
              Saunas involve heat, water, and inherent physical risk. By continuing you acknowledge
              that accidents, serious injury, or even death may occur. This application accepts
              complete and utter zero responsibility for any harm, illness, or property damage
              arising from sauna use, whether as a host or a guest. You agree to use this platform
              lawfully and to take full responsibility for your own safety and the safety of anyone
              accompanying you. Hosts are solely responsible for maintaining a safe environment on
              their property. Lets Go Sauna acts as a marketplace only and makes no warranties regarding any
              listing, user, or sauna condition.
            </p>
          </div>

          {phone && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "16px 0 4px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={smsOptIn}
                onChange={e => setSmsOptIn(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
                Send me SMS booking notifications (requests, confirmations, cancellations). Optional. Message and data rates may apply. Reply <strong>STOP</strong> to opt out.{" "}
                <Link to="/terms" style={{ color: "#2563eb" }}>Terms</Link>
                {" | "}
                <Link to="/privacy" style={{ color: "#2563eb" }}>Privacy</Link>
              </span>
            </label>
          )}

          <p style={{ margin: "10px 0 14px", fontSize: 12, color: "#9ca3af" }}>
            By clicking "Save and continue" or "Skip for now" you agree to the Terms &amp; Conditions above.
          </p>

          <div style={{ display: "flex", gap: 12 }}>
            <button type="submit" disabled={saving} style={primaryBtn}>
              {saving ? "Saving..." : "Save and continue"}
            </button>
            <button type="button" onClick={onDismiss} disabled={saving} style={skipBtn}>
              Skip for now
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
  padding: 16,
};
const modal: React.CSSProperties = {
  position: "relative", background: "#fff", borderRadius: 12,
  padding: "28px 32px 24px", maxWidth: 680, width: "100%",
  maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
};
const closeBtn: React.CSSProperties = {
  position: "absolute", top: 16, right: 16,
  background: "none", border: "none", fontSize: 18, cursor: "pointer",
  color: "#9ca3af", lineHeight: 1, padding: 4,
};
const termsBox: React.CSSProperties = {
  marginTop: 20, padding: "14px 16px",
  background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6,
};
const primaryBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#2563EB", color: "#fff",
  border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 600,
};
const skipBtn: React.CSSProperties = {
  padding: "10px 24px", background: "transparent", color: "#6b7280",
  border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
